import {
  type DeviceDefinitionDto,
  type DiagnosticDefinitionDto,
  type EffectiveParameter,
  type ExamDefinitionDto,
  effectiveParameters,
  type OrderSetReadDto,
  type ProtocolDefinitionDto,
  type TreatmentDefinitionDto,
} from "@repo/data-store";
import { AD_HOC_PROTOCOL_TITLE } from "@repo/domain";
import { codeSystemUrl } from "@repo/fhir-systems";

type RawOrder = OrderSetReadDto["orders"][number];

/** Charted outcomes remain visible even when the request was not fulfilled. */
export { isWithdrawnProcedureOrder as isWithdrawnOrder } from "@repo/data-store";

type ValueDefinition = TreatmentDefinitionDto["effectiveParameters"][number]["value"];

export interface PresentedOrderSetParameterDto {
  /** `"self"` or the device requirement's member id. Never the id alone. */
  source: string;
  /** The required device definition's name; absent for the procedure's own. */
  sourceLabel?: string;
  parameterId: string;
  label: string;
  value:
    | Exclude<ValueDefinition, { kind: "Choice" }>
    | {
        kind: "Choice";
        options: readonly { optionId: string; label: string }[];
        cardinality: "Single" | "Multiple";
        allowFreeText?: boolean;
      };
}

export type PresentedTreatmentFieldDto = {
  key: string;
  componentId: string;
  label: string;
  type: "text" | "number" | "integer" | "boolean" | "choice";
  unit?: string;
  min?: number;
  max?: number;
  step?: number;
  options?: { value: string; label: string }[];
};

export interface PresentedTreatmentDeclarationDto {
  definitionUrl: string;
  version: number;
  charting: {
    code: string;
    label: string;
    description: string;
    category: "treatment";
    soapHint: "objective";
    observations: {
      code: string;
      definitionId: string;
      display: string;
      system: string;
      multiComponent: boolean;
      components?: PresentedTreatmentFieldDto[];
      field?: PresentedTreatmentFieldDto;
    }[];
    procedure: {
      code: string;
      display: string;
      system: string;
      noteField: PresentedTreatmentFieldDto;
    };
  };
}

export interface PresentedDiagnosticDeclarationDto {
  definitionUrl: string;
  version: number;
  name: string;
  description?: string;
  observations: DiagnosticDefinitionDto["observations"];
  procedureNoteLabel?: string;
}

export interface PresentedOrderSetOrderDto extends Omit<RawOrder, "definition"> {
  orderSetId: string;
  /**
   * What this ORDER is called on screen: the definition's name, plus the
   * qualifier when one is needed to tell it from its repeats. Distinct from
   * `definition.title`, which names the catalog revision and is shared by every
   * order that realizes it.
   */
  title: string;
  definition: RawOrder["definition"] & {
    title: string;
    kind: "diagnostic" | "treatment";
    parameters: readonly PresentedOrderSetParameterDto[];
  };
  treatmentDeclaration?: PresentedTreatmentDeclarationDto;
  diagnosticDeclaration?: PresentedDiagnosticDeclarationDto;
}

export interface PresentedOrderSetDto extends Omit<OrderSetReadDto, "steps" | "orders"> {
  /** The protocol's name, or the ad hoc title for a set built without one. */
  title: string;
  steps: readonly (Omit<OrderSetReadDto["steps"][number], "orders"> & {
    orders: readonly PresentedOrderSetOrderDto[];
  })[];
  orders: readonly PresentedOrderSetOrderDto[];
  isTailored: boolean;
  diagnosticProcedures: readonly {
    orderSetId: string;
    orderId: string;
    /** The order's own `title`, so the chart card names it as the orders panel does. */
    title: string;
    orderStatus: RawOrder["status"];
    procedureNote?: string;
    parameters: readonly PresentedOrderSetParameterDto[];
    parameterValues: RawOrder["parameterValues"];
    plannedTrials?: RawOrder["plannedTrials"];
    observationLevels?: RawOrder["observationLevels"];
    executable: boolean;
    definition: PresentedDiagnosticDeclarationDto;
    procedure?: RawOrder["diagnosticProcedure"];
  }[];
}

type Parameter = PresentedOrderSetParameterDto;
type TreatmentDeclaration = PresentedTreatmentDeclarationDto;
type DiagnosticDeclaration = PresentedDiagnosticDeclarationDto;

export interface OrderSetCatalog {
  protocols: readonly ProtocolDefinitionDto[];
  treatments: readonly TreatmentDefinitionDto[];
  diagnostics: readonly DiagnosticDefinitionDto[];
  // Protocol requirements refer to the current exam definition lineage.
  exams: readonly (Pick<ExamDefinitionDto, "definitionUrl" | "name"> & Partial<Pick<ExamDefinitionDto, "version">>)[];
  /** Current revision of every device definition; requirements are unpinned. */
  devices: readonly DeviceDefinitionDto[];
}

export interface OrderSetCatalogQuery extends OrderSetCatalog {
  isLoading: boolean;
  isReady: boolean;
}

/** A set whose pinned catalog revisions are not loaded. It is left out of `data`, not presented with gaps. */
export interface UnavailableOrderSet {
  orderSet: OrderSetReadDto;
  missingCanonicals: readonly string[];
}

export type PresentedOrderSetsState =
  | { status: "loading" }
  | { status: "ready"; data: PresentedOrderSetDto[]; unavailable: UnavailableOrderSet[] };

function canonical(url: string, version: string | number): string {
  return `${url}|${version}`;
}

export function presentOrderSetParameter(effective: EffectiveParameter<ValueDefinition>): Parameter {
  return {
    source: effective.source,
    ...(effective.sourceLabel === undefined ? {} : { sourceLabel: effective.sourceLabel }),
    parameterId: effective.parameterId,
    label: effective.label,
    value:
      effective.value.kind === "Choice"
        ? {
            ...effective.value,
            options: effective.value.options.map((option) => ({ optionId: option.id, label: option.label })),
          }
        : { ...effective.value },
  };
}

/** A presented declaration keyed as the catalog keys it: choice options by `id`. */
export function declaredValue(value: Parameter["value"]): ValueDefinition {
  return value.kind === "Choice"
    ? {
        ...value,
        allowFreeText: value.allowFreeText ?? false,
        options: value.options.map((option) => ({ id: option.optionId, label: option.label })),
      }
    : value;
}

function treatmentField(
  key: string,
  componentId: string,
  label: string,
  value: TreatmentDefinitionDto["effectiveParameters"][number]["value"],
): TreatmentDeclaration["charting"]["procedure"]["noteField"] {
  switch (value.kind) {
    case "Quantity":
      return {
        key,
        componentId,
        label,
        type: "number",
        ...value,
        unit: value.unit?.display,
        ...(value.unit ? { unitCode: value.unit.code } : {}),
      };
    case "Integer":
      return { key, componentId, label, type: "integer", ...value };
    case "Boolean":
      return { key, componentId, label, type: "boolean" };
    case "Text":
      return { key, componentId, label, type: "text" };
    case "Choice":
      return {
        key,
        componentId,
        label,
        type: "choice",
        options: value.options.map((option) => ({ value: option.id, label: option.label })),
      };
  }
}

function treatmentDeclaration(definition: TreatmentDefinitionDto): TreatmentDeclaration {
  return {
    definitionUrl: definition.url,
    version: definition.version,
    charting: {
      code: definition.url,
      label: definition.name,
      description: definition.description ?? "",
      category: "treatment",
      soapHint: "objective",
      observations: definition.chartedObservations.map((observation) => {
        const components = observation.components.map((component) =>
          treatmentField(component.id, component.id, component.label, component.value),
        );
        return {
          code: observation.id,
          definitionId: observation.id,
          display: observation.label,
          system: codeSystemUrl("catalog-observation"),
          multiComponent: components.length > 1,
          ...(components.length === 1 ? { field: components[0] } : { components }),
        };
      }),
      procedure: {
        code: definition.url,
        display: definition.name,
        system: codeSystemUrl("observation"),
        noteField: {
          key: "notes",
          componentId: "notes",
          label: definition.procedureNoteLabel ?? "Notes",
          type: "text",
        },
      },
    },
  };
}

function diagnosticDeclaration(definition: DiagnosticDefinitionDto): DiagnosticDeclaration {
  return {
    definitionUrl: definition.definitionUrl,
    version: definition.version,
    name: definition.name,
    ...(definition.description === undefined ? {} : { description: definition.description }),
    // `chartedObservations`, not `observations`: the latter is the authored list
    // and omits the enabled standards, which silently dropped Tolerance from
    // every diagnostic charting card.
    observations: definition.chartedObservations.map((observation) => ({
      id: observation.id,
      label: observation.label,
      repeatable: observation.repeatable,
      components: observation.components.map((component) => ({
        id: component.id,
        label: component.label,
        value: { ...component.value },
      })),
      ...(observation.noteLabel === undefined ? {} : { noteLabel: observation.noteLabel }),
    })),
    ...(definition.procedureNoteLabel === undefined ? {} : { procedureNoteLabel: definition.procedureNoteLabel }),
  };
}

export function presentOrderSet(orderSet: OrderSetReadDto, catalog: OrderSetCatalog): PresentedOrderSetDto {
  const pin = orderSet.protocol;
  const protocol = pin
    ? catalog.protocols.find(
        (candidate) => canonical(candidate.definitionUrl, candidate.version) === canonical(pin.url, pin.version),
      )
    : undefined;
  const treatments = new Map(
    catalog.treatments.map((definition) => [canonical(definition.url, definition.version), definition]),
  );
  const diagnostics = new Map(
    catalog.diagnostics.map((definition) => [canonical(definition.definitionUrl, definition.version), definition]),
  );
  const devices = new Map(catalog.devices.map((definition) => [definition.definitionUrl, definition]));
  if (pin && !protocol) throw new Error(`Protocol catalog revision '${canonical(pin.url, pin.version)}' is not loaded`);
  const enrich = (order: RawOrder): PresentedOrderSetOrderDto => {
    const key = canonical(order.definition.url, order.definition.version);
    const treatment = treatments.get(key);
    const diagnostic = diagnostics.get(key);
    if (!treatment && !diagnostic) throw new Error(`Order catalog revision '${key}' is not loaded`);
    const requirements = treatment
      ? treatment.deviceRequirements.map((requirement) => ({
          id: requirement.id,
          definitionUrl: requirement.definition,
        }))
      : (diagnostic?.requirements.devices ?? []);
    const definitionTitle = treatment?.name ?? diagnostic?.name ?? order.definition.url;
    return {
      ...order,
      orderSetId: orderSet.orderSetId,
      title: order.qualifier ? `${definitionTitle} — ${order.qualifier}` : definitionTitle,
      definition: {
        ...order.definition,
        title: definitionTitle,
        kind: treatment ? "treatment" : "diagnostic",
        parameters: effectiveParameters<ValueDefinition>(
          treatment?.effectiveParameters ?? diagnostic?.effectiveParameters ?? [],
          requirements,
          devices,
        ).map(presentOrderSetParameter),
      },
      ...(treatment ? { treatmentDeclaration: treatmentDeclaration(treatment) } : {}),
      ...(diagnostic ? { diagnosticDeclaration: diagnosticDeclaration(diagnostic) } : {}),
    };
  };
  const steps = orderSet.steps.map((step) => ({ ...step, orders: step.orders.map(enrich) }));
  const orders = steps.flatMap((step) => step.orders);
  const occurrenceById = new Map(
    protocol?.steps.flatMap((step) =>
      step.occurrences.map((occurrence) => [occurrence.occurrenceId, occurrence] as const),
    ) ?? [],
  );
  const isTailored =
    Boolean(protocol) &&
    orders.some((order) => {
      if (order.status === "revoked") return true;
      const source = order.sourceOccurrenceId ? occurrenceById.get(order.sourceOccurrenceId) : undefined;
      return (
        !source ||
        JSON.stringify(source.parameterValues) !== JSON.stringify(order.parameterValues) ||
        JSON.stringify(source.plannedTrials ?? []) !== JSON.stringify(order.plannedTrials ?? [])
      );
    });
  const diagnosticProcedures = orders.flatMap((order) => {
    if (!order.diagnosticDeclaration) return [];
    return [
      {
        orderSetId: orderSet.orderSetId,
        orderId: order.orderId,
        title: order.title,
        orderStatus: order.status,
        ...(order.procedureNote ? { procedureNote: order.procedureNote } : {}),
        parameters: order.definition.parameters,
        parameterValues: order.parameterValues,
        ...(order.observationLevels ? { observationLevels: order.observationLevels } : {}),
        ...(order.plannedTrials ? { plannedTrials: order.plannedTrials } : {}),
        executable:
          orderSet.status === "active" &&
          Boolean(order.visitId) &&
          order.status !== "completed" &&
          order.status !== "revoked",
        definition: order.diagnosticDeclaration,
        ...(order.diagnosticProcedure ? { procedure: order.diagnosticProcedure } : {}),
      },
    ];
  });

  return {
    ...orderSet,
    title: protocol?.name ?? AD_HOC_PROTOCOL_TITLE,
    steps,
    orders,
    isTailored,
    diagnosticProcedures,
  };
}

export function resolveOrderSetPresentations(
  orderSets: readonly OrderSetReadDto[],
  catalog: OrderSetCatalogQuery,
): PresentedOrderSetsState {
  if (catalog.isLoading || !catalog.isReady) return { status: "loading" };
  const available = new Set([
    ...catalog.protocols.map((definition) => canonical(definition.definitionUrl, definition.version)),
    ...catalog.treatments.map((definition) => canonical(definition.url, definition.version)),
    ...catalog.diagnostics.map((definition) => canonical(definition.definitionUrl, definition.version)),
  ]);
  const data: PresentedOrderSetDto[] = [];
  const unavailable: UnavailableOrderSet[] = [];
  for (const orderSet of orderSets) {
    const required = new Set([
      ...(orderSet.protocol ? [canonical(orderSet.protocol.url, orderSet.protocol.version)] : []),
      ...orderSet.orders.map((order) => canonical(order.definition.url, order.definition.version)),
    ]);
    const missingCanonicals = [...required].filter((identity) => !available.has(identity));
    if (missingCanonicals.length > 0) unavailable.push({ orderSet, missingCanonicals });
    else data.push(presentOrderSet(orderSet, catalog));
  }
  return { status: "ready", data, unavailable };
}
